import { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
    TextField, Chip, Skeleton, Alert, CircularProgress, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import MDButton from '@components/MDButton';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../../CRM/kanban-board/crm-theme';
import { MultichannelService } from '../services/multichannel.service';
import { MultichannelTemplate } from '../services/responses/multichannel.response';
import { fillTemplate, freeVariables } from '@functions/template-variables';

interface Props {
    open: boolean;
    conversationId: number | null;
    inboxId?: number | null;
    onClose: () => void;
    onSent: () => void;
}

export const TemplateDialog = ({ open, conversationId, inboxId, onClose, onSent }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();

    const [templates, setTemplates] = useState<MultichannelTemplate[] | null>(null);
    const [chosen, setChosen] = useState<MultichannelTemplate | null>(null);
    const [values, setValues] = useState<string[]>([]);
    const [sending, setSending] = useState(false);
    /// O que o CRM já preenche; nulo enquanto pergunta, para não mostrar campos descartáveis.
    const [known, setKnown] = useState<Record<string, string> | null>(null);

    useEffect(() => {
        if (!open) return;

        setChosen(null);
        setValues([]);
        setTemplates(null);
        setKnown(null);

        if (conversationId) {
            new MultichannelService().templateValues(conversationId, inboxId)
                .then((reply) => setKnown(reply.data.data ?? {}))
                .catch(() => setKnown({}));
        }

        new MultichannelService().templates()
            .then((reply) => setTemplates(reply.data.data ?? []))
            .catch((err) => {
                showAlert(apiErrorMessage(err), 'error', 3000);
                setTemplates([]);
            });
    }, [open, conversationId, inboxId]);

    const choose = (template: MultichannelTemplate) => {
        setChosen(template);
        setValues(Array(freeVariables(template, known ?? {}).length).fill(''));
    };

    const free = chosen && known ? freeVariables(chosen, known) : [];
    const filled = chosen && known ? chosen.variables.filter((name) => name in known) : [];

    const changeValue = (index: number, value: string) =>
        setValues((current) => current.map((v, i) => (i === index ? value : v)));

    const send = async () => {
        if (!conversationId || !chosen || sending) return;

        setSending(true);
        try {
            await new MultichannelService().sendTemplate(conversationId, inboxId, chosen.name, values);
            onSent();
            onClose();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 4000);
        } finally {
            setSending(false);
        }
    };

    const missingValues = values.some((value) => value.trim().length === 0);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
                {t('crmMultichannel.templates')}
                <IconButton onClick={onClose} aria-label={t('general.fechar')} sx={{ color: cc.textSecondary }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Typography variant="caption" sx={{ color: cc.textSecondary, display: 'block', mb: 2 }}>
                    {t('crmMultichannel.templateExplicacao')}
                </Typography>

                {!templates ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} height={54} variant="rounded" sx={{ mb: 1 }} />
                    ))
                ) : templates.length === 0 ? (
                    <Alert severity="info" icon={false}>
                        <Typography variant="caption">
                            {t('crmMultichannel.semTemplates')}
                        </Typography>
                    </Alert>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {templates.map((template) => {
                            const active = chosen?.name === template.name;

                            return (
                                <Box
                                    key={template.name}
                                    onClick={() => template.usable && known && choose(template)}
                                    sx={{
                                        border: `1px solid ${active ? cc.accent : cc.border}`,
                                        borderRadius: '8px',
                                        p: 1.4,
                                        cursor: template.usable ? 'pointer' : 'not-allowed',
                                        opacity: template.usable ? 1 : 0.55,
                                        bgcolor: active ? cc.countBg : 'transparent',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            mb: 0.5,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight={700}
                                            sx={{ color: cc.textPrimary, fontFamily: 'monospace' }}
                                        >
                                            {template.name}
                                        </Typography>
                                        <Chip
                                            label={template.usable
                                                ? t('crmMultichannel.templateAprovado')
                                                : t('crmMultichannel.templateEmAnalise')}
                                            size="small"
                                            color={template.usable ? 'success' : 'warning'}
                                            variant="outlined"
                                            sx={{ height: 19, fontSize: '10px', '& .MuiChip-label': { color: cc.onTint } }}
                                        />
                                    </Box>

                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: cc.textSecondary,
                                            overflowWrap: 'anywhere',
                                            display: 'block',
                                        }}
                                    >
                                        {template.body}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        sx={{ color: cc.textMuted, fontSize: '10px' }}
                                    >
                                        {template.language} · {template.category}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {filled.length > 0 && (
                    <Box sx={{ mt: 2.5 }}>
                        <Typography
                            variant="caption"
                            sx={{ color: cc.textSecondary, fontWeight: 700, display: 'block', mb: 0.8 }}
                        >
                            {t('crmMultichannel.templatePreenchidasPeloCrm')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {filled.map((name) => (
                                <Chip
                                    key={name}
                                    size="small"
                                    variant="outlined"
                                    label={`${name}: ${known![name]}`}
                                    sx={{ maxWidth: '100%', color: cc.textSecondary, borderColor: cc.border }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {free.length > 0 && (
                    <Box sx={{ mt: 2.5 }}>
                        <Typography
                            variant="caption"
                            sx={{ color: cc.textSecondary, fontWeight: 700, display: 'block', mb: 1 }}
                        >
                            {t('crmMultichannel.templateVariaveis')}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                            {values.map((value, index) => (
                                <TextField
                                    key={index}
                                    size="small"
                                    fullWidth
                                    label={free[index]}
                                    value={value}
                                    onChange={(e) => changeValue(index, e.target.value)}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {chosen && (
                    <Box
                        sx={{
                            mt: 2.5,
                            p: 1.4,
                            borderRadius: '8px',
                            border: `1px dashed ${cc.border}`,
                            bgcolor: cc.cardBg,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ color: cc.textMuted, fontSize: '10px', display: 'block', mb: 0.5 }}
                        >
                            {t('crmMultichannel.templatePreVisualizacao')}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: cc.textPrimary,
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {fillTemplate(chosen, known ?? {}, values)}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <MDButton variant="gradient" color="dark" onClick={onClose}>
                    {t('general.cancelar')}
                </MDButton>
                <MDButton
                    variant="gradient"
                    color="info"
                    disabled={!chosen || missingValues || sending}
                    onClick={send}
                >
                    {sending ? <CircularProgress size={16} /> : t('crmMultichannel.templateEnviar')}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};
